import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Plus, Package, Loader2, Languages, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import AdminLogin from './AdminLogin';
import AdminHeader from './AdminHeader';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { ServiceCard, ServiceForm } from './admin';
import { apiUrl, API_URL } from '../utils/config';
import logger from '../utils/logger';

// Delete confirmation modal component
const DeleteConfirmModal = ({ show, title, onConfirm, onCancel }) => {
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4 shadow-2xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <Package className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Delete Service</h3>
            <p className="text-sm text-gray-500">This action cannot be undone</p>
          </div>
        </div>
        <p className="text-gray-700 mb-6">
          Are you sure you want to delete <strong>"{title}"</strong>?
        </p>
        <div className="flex gap-3">
          <Button variant="outline" onClick={onCancel} className="flex-1">
            Cancel
          </Button>
          <Button onClick={onConfirm} className="flex-1 bg-red-600 hover:bg-red-700 text-white">
            Yes, Delete
          </Button>
        </div>
      </div>
    </div>
  );
};

// Initial form state
const getInitialFormData = () => ({
  title_en: '',
  title_tr: '',
  description_en: '',
  description_tr: '',
  price: '',
  price_type: 'one-time',
  subscription_period: '',
  duration: '',
  features_en: [''],
  features_tr: [''],
  image: '',
  category_en: '',
  category_tr: '',
  service_type: 'corporate',
  featured: false,
  active: true,
  order: 0
});

const ServicesAdmin = ({ onBackToHome }) => {
  const { isAuth, isCheckingAuth, handleLogin, handleLogout, getAuthHeader } = useAdminAuth();
  const [services, setServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState('en');
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false, serviceId: null, title: '' });
  const [translatingId, setTranslatingId] = useState(null);
  const [isTranslatingAll, setIsTranslatingAll] = useState(false);
  const [formData, setFormData] = useState(getInitialFormData());

  // Check if a service is missing Turkish translations
  const isMissingTurkish = useCallback((service) => {
    return !service.title_tr || !service.description_tr || 
           !service.features_tr || service.features_tr.length === 0 ||
           service.features_tr.every(f => !f);
  }, []);

  const fetchServices = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await fetch(apiUrl('services/admin'));
      if (response.ok) {
        const data = await response.json();
        setServices(data);
      }
    } catch (error) {
      logger.error('Error fetching services:', error);
      toast.error('Failed to load services');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAuth) {
      fetchServices();
    }
  }, [isAuth, fetchServices]);

  const resetForm = useCallback(() => {
    setFormData(getInitialFormData());
    setImagePreview(null);
    setEditingService(null);
    setActiveTab('en');
  }, []);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await fetch(apiUrl('upload'), {
        method: 'POST',
        body: formDataUpload
      });

      if (response.ok) {
        const data = await response.json();
        const imageUrl = `${API_URL}${data.url}`;
        setFormData(prev => ({ ...prev, image: imageUrl }));
        setImagePreview(imageUrl);
        toast.success('Image uploaded');
      } else {
        toast.error('Failed to upload image');
      }
    } catch (error) {
      logger.error('Upload error:', error);
      toast.error('Failed to upload image');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        order: parseInt(formData.order) || 0,
        features_en: formData.features_en.filter(f => f.trim()),
        features_tr: formData.features_tr.filter(f => f.trim()),
        subscription_period: formData.price_type === 'subscription' ? formData.subscription_period : null
      };

      const url = editingService 
        ? apiUrl(`services/${editingService.id}`)
        : apiUrl('services');
      
      const response = await fetch(url, {
        method: editingService ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingService ? 'Service updated!' : 'Service created!');
        setShowForm(false);
        resetForm();
        fetchServices();
      } else {
        const error = await response.json();
        let errorMessage = 'Failed to save service';
        if (typeof error.detail === 'string') {
          errorMessage = error.detail;
        } else if (Array.isArray(error.detail)) {
          errorMessage = error.detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        }
        toast.error(errorMessage);
      }
    } catch (error) {
      logger.error('Error saving service:', error);
      toast.error('Failed to save service');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = useCallback((service) => {
    setEditingService(service);
    setFormData({
      title_en: service.title_en || '',
      title_tr: service.title_tr || '',
      description_en: service.description_en || '',
      description_tr: service.description_tr || '',
      price: service.price?.toString() || '',
      price_type: service.price_type || 'one-time',
      subscription_period: service.subscription_period || '',
      duration: service.duration || '',
      features_en: service.features_en?.length ? service.features_en : [''],
      features_tr: service.features_tr?.length ? service.features_tr : [''],
      image: service.image || '',
      category_en: service.category_en || '',
      category_tr: service.category_tr || '',
      service_type: service.service_type || 'corporate',
      featured: service.featured || false,
      active: service.active !== false,
      order: service.order || 0
    });
    setImagePreview(service.image);
    setShowForm(true);
  }, []);

  const handleDuplicate = useCallback((service) => {
    setEditingService(null);
    setFormData({
      title_en: `${service.title_en || ''} (Copy)`,
      title_tr: `${service.title_tr || ''} (Kopya)`,
      description_en: service.description_en || '',
      description_tr: service.description_tr || '',
      price: service.price?.toString() || '',
      price_type: service.price_type || 'one-time',
      subscription_period: service.subscription_period || '',
      duration: service.duration || '',
      features_en: service.features_en?.length ? [...service.features_en] : [''],
      features_tr: service.features_tr?.length ? [...service.features_tr] : [''],
      image: service.image || '',
      category_en: service.category_en || '',
      category_tr: service.category_tr || '',
      service_type: service.service_type || 'corporate',
      featured: false,
      active: true,
      order: services.length
    });
    setImagePreview(service.image);
    setShowForm(true);
    toast.info('Service duplicated. Make your changes and save.');
  }, [services.length]);

  const handleDelete = useCallback((serviceId, title) => {
    setDeleteConfirm({ show: true, serviceId, title });
  }, []);

  const confirmDelete = async () => {
    const { serviceId } = deleteConfirm;
    try {
      const response = await fetch(apiUrl(`services/${serviceId}`), {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (response.ok) {
        toast.success('Service deleted');
        fetchServices();
      } else {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to delete service');
      }
    } catch {
      toast.error('Network error: Failed to delete service');
    } finally {
      setDeleteConfirm({ show: false, serviceId: null, title: '' });
    }
  };

  const handleToggleActive = useCallback(async (service) => {
    try {
      const response = await fetch(apiUrl(`services/${service.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...service, active: !service.active })
      });
      if (response.ok) {
        toast.success(service.active ? 'Service deactivated' : 'Service activated');
        fetchServices();
      }
    } catch {
      toast.error('Failed to update service');
    }
  }, [fetchServices]);

  const handleToggleFeatured = useCallback(async (service) => {
    try {
      const response = await fetch(apiUrl(`services/${service.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...service, featured: !service.featured })
      });
      if (response.ok) {
        toast.success(service.featured ? 'Removed from featured' : 'Added to featured');
        fetchServices();
      }
    } catch {
      toast.error('Failed to update service');
    }
  }, [fetchServices]);

  const handleAutoTranslate = useCallback(async (serviceId) => {
    setTranslatingId(serviceId);
    try {
      const response = await fetch(apiUrl(`services/${serviceId}/auto-translate`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        }
      });

      if (response.ok) {
        const result = await response.json();
        if (result.translated_fields?.length > 0) {
          toast.success(`Translated ${result.translated_fields.length} fields to Turkish`);
          fetchServices();
        } else {
          toast.info('No fields needed translation');
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Translation failed');
      }
    } catch (error) {
      logger.error('Translation error:', error);
      toast.error('Failed to translate service');
    } finally {
      setTranslatingId(null);
    }
  }, [getAuthHeader, fetchServices]);

  const handleTranslateAll = async () => {
    const toTranslate = services.filter(isMissingTurkish);
    if (toTranslate.length === 0) {
      toast.info('All services already have Turkish translations');
      return;
    }

    setIsTranslatingAll(true);
    let successCount = 0;
    let errorCount = 0;

    for (const service of toTranslate) {
      try {
        const response = await fetch(apiUrl(`services/${service.id}/auto-translate`), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader()
          }
        });

        if (response.ok) {
          const result = await response.json();
          if (result.translated_fields?.length > 0) {
            successCount++;
          }
        } else {
          errorCount++;
        }
      } catch {
        errorCount++;
      }
    }

    setIsTranslatingAll(false);
    fetchServices();

    if (successCount > 0) {
      toast.success(`Successfully translated ${successCount} services to Turkish`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to translate ${errorCount} services`);
    }
  };

  // Derived state
  const servicesMissingTurkish = services.filter(isMissingTurkish);

  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!isAuth) {
    return <AdminLogin onLogin={handleLogin} title="Services Admin" />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminHeader
        title="Services Admin"
        onBackToHome={onBackToHome}
        onLogout={handleLogout}
        onAddNew={() => { resetForm(); setShowForm(true); }}
        addNewLabel="Add Service"
      />

      <div className="container mx-auto px-6 py-8">
        {/* Turkish Translation Warning Banner */}
        {!isLoading && servicesMissingTurkish.length > 0 && (
          <div className="mb-6 bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center justify-between" data-testid="turkish-translation-warning">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <h4 className="font-semibold text-orange-800">Missing Turkish Translations</h4>
                <p className="text-sm text-orange-700">
                  {servicesMissingTurkish.length} service{servicesMissingTurkish.length > 1 ? 's' : ''} will not display properly when visitors select Turkish language.
                </p>
              </div>
            </div>
            <Button
              onClick={handleTranslateAll}
              disabled={isTranslatingAll}
              className="bg-orange-600 hover:bg-orange-700 text-white"
              data-testid="translate-all-btn"
            >
              {isTranslatingAll ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Translating...
                </>
              ) : (
                <>
                  <Languages className="w-4 h-4 mr-2" />
                  Auto-Translate All
                </>
              )}
            </Button>
          </div>
        )}

        {/* Services List */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
          </div>
        ) : services.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl shadow-md">
            <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">No Services Yet</h3>
            <p className="text-gray-500 mb-6">Create your first service to start selling</p>
            <Button onClick={() => setShowForm(true)} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Service
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((service) => (
              <ServiceCard
                key={service.id}
                service={service}
                onEdit={handleEdit}
                onDuplicate={handleDuplicate}
                onDelete={handleDelete}
                onToggleActive={handleToggleActive}
                onToggleFeatured={handleToggleFeatured}
                onAutoTranslate={handleAutoTranslate}
                isTranslating={translatingId === service.id}
                isMissingTurkish={isMissingTurkish(service)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Service Form Modal */}
      {showForm && (
        <ServiceForm
          formData={formData}
          setFormData={setFormData}
          editingService={editingService}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          imagePreview={imagePreview}
          fileInputRef={fileInputRef}
          onImageUpload={handleImageUpload}
          onSubmit={handleSubmit}
          onClose={() => { setShowForm(false); resetForm(); }}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        show={deleteConfirm.show}
        title={deleteConfirm.title}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteConfirm({ show: false, serviceId: null, title: '' })}
      />
    </div>
  );
};

export default ServicesAdmin;
